import { z } from 'zod';

declare global {
  interface Window {
    // Injected per-environment by entrypoint.sh into config.js. Empty in dev.
    __APP_CONFIG__?: Record<string, string>;
  }
}

const Env = z.object({
  // Optional: when unset, requests go to the same origin the app is served from.
  VITE_BACKEND_URL: z.url().optional(),
  VITE_CPMS_BACKEND_URL: z.url().optional(),
  VITE_ZUMO_TOKEN: z.string().min(1).optional(),
  // Cognito is required — the app always authenticates against a real user pool.
  VITE_COGNITO_USER_POOL_ID: z.string().min(1),
  VITE_COGNITO_USER_POOL_CLIENT_ID: z.string().min(1),
  VITE_COGNITO_ENDPOINT: z.url().optional(),
  VITE_SENTRY_DSN: z.url().optional(),
  // Feature flags (UX-only, never security — anything here is visible in config.js).
  // Injected per-environment via entrypoint.sh; flip by changing the env var and
  // restarting the container. Read through @/featureFlags rather than directly.
  VITE_FEATURE_FLAG_GLOBAL_SEARCH: z.stringbool().default(false),
  VITE_FEATURE_FLAG_GLOBAL_NOTIFICATIONS: z.stringbool().default(false),
  MODE: z.enum(['development', 'production', 'test']),
});

// Runtime config (config.js → window.__APP_CONFIG__) is injected per-environment at
// container start and wins over build-time import.meta.env. In dev __APP_CONFIG__ is an
// empty stub, so we fall back to import.meta.env from .env.*.local. Empty strings are
// dropped so unset optional URL fields stay undefined instead of failing z.url() on "".
const runtimeConfig = typeof window !== 'undefined' ? (window.__APP_CONFIG__ ?? {}) : {};
const runtimeEntries = Object.fromEntries(
  Object.entries(runtimeConfig).filter(([, value]) => value !== ''),
);

const parsed = Env.safeParse({
  ...import.meta.env,
  ...runtimeEntries,
  MODE: import.meta.env.MODE,
});
if (!parsed.success) {
  console.error('[env] Invalid configuration:', z.flattenError(parsed.error).fieldErrors);
  throw new Error('Invalid environment configuration. See console for details.');
}

export const env = parsed.data;
